import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { CreateRouteSchema } from "../../schemas/index";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/Form";
import { Checkbox } from "../ui/Checkbox";
import { z } from "zod";
import { Combobox } from "../ui/Combobox";
import { useEffect, useState } from "react";
import { City } from "../../api/locations/location.responses";
import { Sponsor } from "../../api/sponsors/sponsor.responses";
import useSponsorService from "../../api/sponsors/sponsor.service";
import useLocationService from "../../api/locations/location.service";
import { CreateRouteData } from "../../api/edits/editRoute.requests";
import useEditRouteService from "../../api/edits/editRoute.service";
import useLanguageService from "../../api/lanugages/language.service";
import { Language } from "../../api/lanugages/language.responses";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/Card";
import { useNavigate } from "react-router-dom";

interface CreateRouteCardFormProps {
  handleBackClick: () => void;
}

function CreateRouteCardForm({ handleBackClick }: CreateRouteCardFormProps) {
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);

  const navigate = useNavigate();
  const { getSponsors } = useSponsorService();
  const { createRoute } = useEditRouteService();
  const { getAllCities } = useLocationService();
  const { getLanguages } = useLanguageService();

  const form = useForm({
    resolver: zodResolver(CreateRouteSchema),
    defaultValues: {
      appleStoreId: undefined,
      playStoreId: undefined,
      timeDuration: undefined,
      isFree: true,
      previewImagePath: undefined,
      cityId: 0,
      sponsorId: 0,
      languageCode: "",
      name: "",
    },
  });

  const onSubmit = async (data: z.infer<typeof CreateRouteSchema>) => {
    //Prepare post data
    const dataToSend: CreateRouteData = {
      ...data,
    };
    try {
      const routeState = await createRoute(dataToSend);

      if (routeState.status === 201) {
        navigate(`/${routeState.data.id}`);
      }
    } catch (error: any) {
      console.error(
        "Something went wrong while creating route",
        JSON.stringify(error, null, 2)
      );
      if (error.response) {
        alert("Data:" + JSON.stringify(error.response.data, null, 2));
      } else {
        alert(`Error: ${error.message}`);
      }
    }
  };

  useEffect(() => {
    async function fetchSponsors(): Promise<Sponsor[]> {
      try {
        const sponsors = (await getSponsors()).data;
        setSponsors(sponsors);

        return sponsors;
      } catch (err: any) {
        if (err.response) {
          alert("Data:" + JSON.stringify(err.response.data, null, 2));
        } else {
          alert(`Error: ${err.message}`);
        }
        return [];
      }
    }
    async function fetchCities(): Promise<City[]> {
      try {
        const cities = (await getAllCities()).data;
        setCities(cities);

        return cities;
      } catch (err: any) {
        if (err.response) {
          alert("Data:" + JSON.stringify(err.response.data, null, 2));
        } else {
          alert(`Error: ${err.message}`);
        }
        return [];
      }
    }

    async function fetchLanguages(): Promise<Language[]> {
      try {
        const languagesFromDb = (await getLanguages()).data;
        setLanguages(languagesFromDb);

        return languagesFromDb;
      } catch (err: any) {
        if (err.response) {
          alert("Data:" + JSON.stringify(err.response.data, null, 2));
        } else {
          alert(`Error: ${err.message}`);
        }
        return [];
      }
    }

    fetchLanguages();
    fetchCities();
    fetchSponsors();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create New Route</CardTitle>
        <CardDescription>* - required fields</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <div className="grid grid-cols-2 gap-6">
              {/* Left column */}
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="appleStoreId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Apple store ID</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="playStoreId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Play store ID</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="timeDuration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">Time duration</FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="eg. 1h, 1h 30min, 50min,..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isFree"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="text-base">
                          *This route is free
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right column */}
              <div className="space-y-6">
                <FormField
                  control={form.control}
                  name="previewImagePath"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">
                        Preview image path
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="text"
                          placeholder="e.g image.jpg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="cityId"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-base">*City</FormLabel>
                      <FormControl>
                        <Combobox<City>
                          label="city"
                          options={cities}
                          selectedOption={
                            cities.find((city) => city.id === field.value) || null
                          }
                          onOptionChange={(option) =>
                            field.onChange(option ? option.id : 0)
                          }
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id.toString()}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sponsorId"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-base">*Sponsor</FormLabel>
                      <FormControl>
                        <Combobox<Sponsor>
                          label="sponsor"
                          options={sponsors}
                          selectedOption={
                            sponsors.find(
                              (sponsor) => sponsor.id === field.value
                            ) || null
                          }
                          onOptionChange={(option) =>
                            field.onChange(option ? option.id : 0)
                          }
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.id.toString()}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="languageCode"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel className="text-base">*Language</FormLabel>
                      <FormControl>
                        <Combobox<Language>
                          label="language"
                          options={languages}
                          selectedOption={
                            languages.find(
                              (language) => language.code === field.value
                            ) || null
                          }
                          onOptionChange={(option) =>
                            field.onChange(option ? option.code : 0)
                          }
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.code}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Full-width route name below both columns */}
              <div className="col-span-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-base">
                        *Route name (in lang)
                      </FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <Button className="w-full" type="submit">
              Create
            </Button>
            <Button
              className="w-full"
              variant="outline"
              onClick={handleBackClick}
            >
              Back
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

export default CreateRouteCardForm;
